import { computed, inject, Injectable, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NavController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { ComponentStore, tapResponse } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { setUser } from '@sentry/angular-ivy';
import { TuiAlertService } from '@taiga-ui/core';
import { ENVIRONMENT } from '@us/app-config';
import { NonNullableGenericState } from '@us/web/shared/data-access';
import { getErrorMessage, RouteUtil } from '@us/web/shared/utils';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { OtpProvider } from '../enums';
import { FbLoginResponse, LoginParams, LoginResponse } from '../models';
import { AuthService } from '../services/auth.service';
import { AuthActions } from './auth.actions';
import { selectAllowLoginViaOtp } from '@us/web/shop/data-access';
import { HttpErrorResponse } from '@angular/common/http';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const FB: any;

export type LoginState = NonNullableGenericState<{
    phoneNumber: string | null;
    otpProvider: OtpProvider | null;
    otpSent: boolean;
    resetPinCode?: boolean;
    showOtpVerifyFormDialog: boolean;
    showPinCodeFormDialog: boolean;
    /**
     * Due to need to init FB SDK
     */
    allowLoginViaFacebook: boolean;
}>;

@Injectable()
export class LoginStore extends ComponentStore<LoginState> {
    private readonly authService = inject(AuthService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly navCtrl = inject(NavController);
    private readonly alertService = inject(TuiAlertService);
    private readonly translocoService = inject(TranslocoService);
    private readonly store = inject(Store);
    private readonly env = inject(ENVIRONMENT);

    readonly phoneNumber = this.selectSignal(s => s.data.phoneNumber || null) as Signal<string | null>;
    readonly otpProvider = this.selectSignal(s => s.data.otpProvider || null) as Signal<OtpProvider | null>;
    readonly isLoading = this.selectSignal(s => s.status === 'loading');
    readonly showOtpVerifyFormDialog = this.selectSignal(s => s.data.showOtpVerifyFormDialog);
    readonly showPinCodeFormDialog = this.selectSignal(s => s.data.showPinCodeFormDialog);
    readonly allowLoginViaFacebook = this.selectSignal(s => s.data.allowLoginViaFacebook);
    readonly resetPinCode = this.selectSignal(s => s.data.resetPinCode);
    readonly allowLoginViaOtp = this.store.selectSignal(selectAllowLoginViaOtp);
    readonly otpSent = this.selectSignal(s => s.data.otpSent);

    readonly usePinCode = computed(() => this.otpProvider() === OtpProvider.PIN_CODE);
    /**
     * @deprecated
     * TODO: remove this var
     */
    readonly otpProviders = computed(() => {
        const providers = [];

        if (this.allowLoginViaOtp()) {
            providers.push({ untranslated_label: 'send_via_sms', value: OtpProvider.SMS, icon: 'icon::sms' });
        }
        // { untranslated_label: 'call_me', value: OtpProvider.CALL, icon: 'icon::phone' }

        return providers;
    });

    constructor() {
        super(<LoginState>{
            status: 'pending',
            data: {
                phoneNumber: null,
                otpProvider: null,
                otpSent: false,
                showOtpVerifyFormDialog: false,
                allowLoginViaFacebook: false,
                showPinCodeFormDialog: false
            },
            error: null
        });
    }

    readonly setShowOtpVerifyFormDialog = this.updater(
        (state, showOtpVerifyFormDialog: boolean): LoginState => ({
            ...state,
            data: { ...state.data, showOtpVerifyFormDialog }
        })
    );

    readonly setAllowLoginViaFacebook = this.updater(
        (state, allowLoginViaFacebook: boolean): LoginState => ({
            ...state,
            data: { ...state.data, allowLoginViaFacebook }
        })
    );

    readonly setShowPinCodeFormDialog = this.updater(
        (state, showPinCodeFormDialog: boolean): LoginState => ({
            ...state,
            data: { ...state.data, showPinCodeFormDialog }
        })
    );

    readonly verifyPhoneNumber = this.effect(
        (
            params$: Observable<{
                phoneNumber: string;
            }>
        ) =>
            params$.pipe(
                tap(() => {
                    this.patchState(state => ({
                        ...state,
                        status: 'loading',
                        data: { ...state.data },
                        error: null
                    }));
                }),
                switchMap(({ phoneNumber }) =>
                    this.authService.verifyPhoneNumber({ phoneNumber }).pipe(
                        tapResponse(
                            response => {
                                const otpProvider = response.pin_code_exist
                                    ? OtpProvider.PIN_CODE
                                    : this.allowLoginViaOtp()
                                    ? OtpProvider.SMS
                                    : null;

                                this.patchState(state => ({
                                    ...state,
                                    status: 'success',
                                    data: {
                                        ...state.data,
                                        phoneNumber,
                                        otpProvider,
                                        showOtpVerifyFormDialog: otpProvider !== null
                                    },
                                    error: null
                                }));

                                if (otpProvider === OtpProvider.SMS) {
                                    this.sendOtp({
                                        phoneNumber,
                                        otpProvider
                                    });
                                }
                            },
                            error => {
                                console.error(getErrorMessage(error));
                                this.patchState({
                                    status: 'error',
                                    error: getErrorMessage(error)
                                });
                            }
                        )
                    )
                )
            )
    );

    readonly sendOtp = this.effect(
        (
            params$: Observable<{
                phoneNumber: string;
                otpProvider: OtpProvider;
                onSuccess?: () => void;
            }>
        ) =>
            params$.pipe(
                tap(({ phoneNumber, otpProvider }) => {
                    this.patchState(state => ({
                        ...state,
                        status: 'loading',
                        data: {
                            ...state.data,
                            phoneNumber,
                            otpProvider: otpProvider,
                            otpSent: false,
                            showOtpVerifyFormDialog: state.data.showOtpVerifyFormDialog
                        },
                        error: null
                    }));
                }),
                switchMap(({ phoneNumber, onSuccess }) =>
                    this.authService.sendOtp({ phone_number: phoneNumber }).pipe(
                        tapResponse(
                            async ({ otp_provider: otpProvider }) => {
                                this.patchState(state => ({
                                    ...state,
                                    status: 'success',
                                    data: { ...state.data, otpSent: true, showOtpVerifyFormDialog: true, otpProvider },
                                    error: null
                                }));

                                onSuccess?.();
                            },
                            (error: HttpErrorResponse) => {
                                const message = getErrorMessage(error);
                                this.patchState(state => ({
                                    ...state,
                                    status: 'error',
                                    error: message
                                }));

                                this.alertService.open(message, { status: 'error' }).subscribe();
                            }
                        )
                    )
                ),
                catchError((error: any) => {
                    console.error(error);
                    this.patchState({
                        status: 'error',
                        error: error
                    });
                    // this.alertService.open(message, { status: 'error' }).subscribe();
                    return of(null);
                })
            )
    );

    readonly resendOtp = this.effect((params$: Observable<void>) =>
        params$.pipe(
            map(() => {
                return this.sendOtp({
                    phoneNumber: this.phoneNumber() as string,
                    otpProvider: this.otpProvider() as OtpProvider
                });
            })
        )
    );

    readonly forgotPassword = this.effect((params$: Observable<void>) =>
        params$.pipe(
            tap(() => {
                this.patchState(state => ({
                    ...state,
                    data: {
                        ...state.data,
                        resetPinCode: true,
                        otpProvider: OtpProvider.SMS
                    }
                }));
            }),
            map(() => {
                return this.sendOtp({
                    phoneNumber: this.phoneNumber() as string,
                    otpProvider: OtpProvider.SMS
                });
            })
        )
    );

    readonly verifyOtp = this.effect((params$: Observable<{ otp: string; onSuccess?: () => void }>) =>
        params$.pipe(
            tap(() => {
                this.patchState({
                    status: 'loading',
                    error: null
                });
            }),
            switchMap(({ otp, onSuccess }) => {
                const params = {
                    otp,
                    phone_number: this.phoneNumber() as string,
                    otp_provider: this.otpProvider() as OtpProvider,
                    reset_pin_code: this.resetPinCode()
                };

                return this.authService.verifyOtp(params).pipe(
                    tapResponse(
                        async response => {
                            await this.onLoginSuccess(response);
                            onSuccess?.();
                        },
                        error => this.onLoginError(error)
                    )
                );
            })
        )
    );

    readonly loginViaFacebook = this.effect<void>((params$: Observable<void>) =>
        params$.pipe(
            tap(() => {
                FB.login(
                    (response: FbLoginResponse) => {
                        if (response.authResponse) {
                            this.loginViaSocial({
                                user: {
                                    provider: 'FACEBOOK',
                                    access_token: response.authResponse.accessToken,
                                    id: response.authResponse.userID
                                }
                            });
                        } else {
                            console.log('User cancelled login or did not fully authorize.');
                        }
                    },
                    { scope: 'public_profile,email' }
                );
            })
        )
    );

    private readonly loginViaSocial = this.effect((params$: Observable<{ user: LoginParams }>) =>
        params$.pipe(
            tap(() => {
                this.patchState({
                    status: 'loading',
                    error: null
                });
            }),
            switchMap(({ user }) => {
                return this.authService.login(user).pipe(
                    tapResponse(
                        response => this.onLoginSuccess(response),
                        error => this.onLoginError(error)
                    )
                );
            })
        )
    );

    private async onLoginSuccess(response: LoginResponse) {
        this.patchState({
            status: 'success',
            data: {
                phoneNumber: null,
                otpProvider: null,
                otpSent: false,
                showOtpVerifyFormDialog: false,
                allowLoginViaFacebook: false,
                showPinCodeFormDialog: response.pin_code_exist === false
            },
            error: null
        });

        this.alertService
            .open(this.translocoService.translate('logged_in_successfully'), { status: 'success' })
            .subscribe();

        const { access_token, token_type, expires_in, api_domain, ...user } = response;
        this.store.dispatch(
            AuthActions.loginSuccess({
                user,
                token: { access_token, token_type, expires_in, api_domain }
            })
        );

        if (this.env.production) {
            setUser({
                id: user.id.toString(),
                username: user.username || user.phone,
                email: user.email || undefined
            });
        }

        if (response.pin_code_exist !== false || this.resetPinCode()) {
            const returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'];
            await this.navCtrl.navigateRoot(returnUrl || RouteUtil.getHomeRouteUrl());
        }
    }

    private onLoginError(error: unknown) {
        const message = getErrorMessage(error);
        this.patchState({
            status: 'error',
            error: message
        });
        this.alertService.open(message, { status: 'error' }).subscribe();
    }
}
